import {AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {ActivatedRoute} from '@angular/router';
import {
    BucketName,
    ClientLegitimacyConfirmation,
    ConfirmationMethod,
    DocumentAttachment,
    IncidentTransaction,
    IncidentTransactionRequestV2,
    IncidentType,
    LoanInfoRequest,
    ReferenceBookResponse,
    RequisiteType,
    SubjectType,
    TransactionCategory,
    TransactionType,
} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, getFormattedDateOrNull, Spinner} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {BehaviorSubject, Subscription} from 'rxjs';
import {DateForm} from '../../../../../components/date-picker-field/date-picker-field.component';
import {DictionarySelectorComponent} from '../../../../../components/dictionary/dictionary-selector.component';
import {
    AttachmentsForm,
    attachmentsRequiredValidator,
    buildAttachmentsForm,
    createAttachmentsRequest,
    createAttachmentsRequestV2,
} from '../../../../../components/files/files.component';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {MIN_DATE} from '../../../../../domain/constants';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {INCIDENT_TYPE_CODE} from '../../../../../domain/query-params.keys';
import {DATE_TIME_FORMATS} from '../../../../../infra/date-time.adapter';
import {DateTimeValidators} from '../../../../../infra/date-time.validator';
import {sanitizeDigits} from '../../create-edit-incident.component';
import {buildMerchantInfoForm, createMerchantInfoRequest, MerchantInfoForm} from './payment-information/payment-information.component';
import {
    createParticipantRequest,
    createParticipantRequestV2,
    NO_EXTRA_REQUISITE_BLOCKS,
    ParticipantForm,
    REQUISITE_ISSUER_BIN_CONTROL,
    TransactionParticipantComponent,
} from './transaction-participant/transaction-participant.component';

export const MIN_RRN_BRN_LENGTH = 12;
export const MAX_RRN_BRN_LENGTH = 16;
const CREDIT_ORG_TYPES = ['BANK', 'MFO'];
const CASH_TRANSACTION_CATEGORY = 'C';
interface TransactionTypeConfig {
    kind: 'payment' | 'transfer' | 'cash';
    tooltipKey?: string;
    senderSubjectType?: SubjectType;
    senderRequisiteType?: RequisiteType;
    receiverRequisiteType?: RequisiteType;
    senderAllowedRequisiteTypes?: RequisiteType[];
    receiverAllowedRequisiteTypes?: RequisiteType[];
    senderExtraRequisiteBlocks?: RequisiteType[];
    receiverExtraRequisiteBlocks?: RequisiteType[];
    phoneRequired?: boolean;
    miaRequisitePrefillSkipped?: boolean;
}

const TRANSACTION_TYPE_CONFIG: Record<string, TransactionTypeConfig> = {
    [TransactionType.PAYPOS]: {
        kind: 'payment',
        tooltipKey: 'i18n_af_tooltipTransactionTypePayPos',
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.PAYMENT_POINT,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.PAYQR]: {
        kind: 'payment',
        tooltipKey: 'i18n_af_tooltipTransactionTypePayQr',
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.PAYMENT_POINT,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPPB]: {
        kind: 'payment',
        tooltipKey: 'i18n_af_tooltipTransactionTypeTopPb',
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPOTH]: {
        kind: 'payment',
        tooltipKey: 'i18n_af_tooltipTransactionTypeTopOth',
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPEWP]: {
        kind: 'payment',
        tooltipKey: 'i18n_af_tooltipTransactionTypeTopEwp',
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.ELECTRONIC_WALLET,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.ELECTRONIC_WALLET],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPCW]: {
        kind: 'payment',
        tooltipKey: 'i18n_af_tooltipTransactionTypeTopCw',
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.ELECTRONIC_WALLET,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.ELECTRONIC_WALLET],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.C2C]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeC2c',
        senderSubjectType: SubjectType.PERSON,
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
    },
    [TransactionType.C2A]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeC2a',
        senderSubjectType: SubjectType.PERSON,
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
    },
    [TransactionType.A2C]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeA2c',
        senderSubjectType: SubjectType.PERSON,
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
    },
    [TransactionType.A2A]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeA2a',
        senderSubjectType: SubjectType.PERSON,
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
    },
    [TransactionType.CORPC2A]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeCorpC2a',
        senderSubjectType: SubjectType.ORGANIZATION,
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
    },
    [TransactionType.CORPC2C]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeCorpC2c',
        senderSubjectType: SubjectType.ORGANIZATION,
        senderRequisiteType: RequisiteType.CARD_MASK,
        receiverRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
    },
    [TransactionType.CORPA2A]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeCorpA2a',
        senderSubjectType: SubjectType.ORGANIZATION,
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
    },
    [TransactionType.CORPA2C]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeCorpA2c',
        senderSubjectType: SubjectType.ORGANIZATION,
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
    },
    [TransactionType.P2P]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeP2p',
        phoneRequired: true,
        miaRequisitePrefillSkipped: true,
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
    },
    [TransactionType.P2PO]: {
        kind: 'transfer',
        tooltipKey: 'i18n_af_tooltipTransactionTypeP2po',
        phoneRequired: true,
        miaRequisitePrefillSkipped: true,
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
    },
    [TransactionType.CW]: {
        kind: 'cash',
        senderRequisiteType: RequisiteType.ACCOUNT_KZ,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ, RequisiteType.CARD_MASK],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.CBC]: {
        kind: 'cash',
        senderRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ, RequisiteType.CARD_MASK],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.WATM]: {
        kind: 'cash',
        senderRequisiteType: RequisiteType.CARD_MASK,
        senderAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ, RequisiteType.CARD_MASK],
        senderExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPUPCACD]: {
        kind: 'cash',
        receiverRequisiteType: RequisiteType.ACCOUNT_KZ,
        receiverAllowedRequisiteTypes: [RequisiteType.ACCOUNT_KZ],
        receiverExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPUPCCD]: {
        kind: 'cash',
        receiverRequisiteType: RequisiteType.CARD_MASK,
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPUPCATM]: {
        kind: 'cash',
        receiverRequisiteType: RequisiteType.CARD_MASK,
        receiverAllowedRequisiteTypes: [RequisiteType.CARD_MASK],
        receiverExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
    [TransactionType.TOPEWT]: {
        kind: 'cash',
        receiverRequisiteType: RequisiteType.ELECTRONIC_WALLET,
        receiverAllowedRequisiteTypes: [RequisiteType.ELECTRONIC_WALLET],
        receiverExtraRequisiteBlocks: [RequisiteType.PAYMENT_POINT],
    },
};
const ALL_REQUISITE_TYPES: RequisiteType[] = [
    RequisiteType.CARD_MASK,
    RequisiteType.ACCOUNT_KZ,
    RequisiteType.ACCOUNT_FOREIGN_BANK,
    RequisiteType.ELECTRONIC_WALLET,
    RequisiteType.PAYMENT_POINT,
];
const NO_EXCLUDED_REQUISITE_TYPES: string[] = [];
const COMPLETED_TRANSACTION_STATUS = 'C';
const INCOMPLETE_TRANSACTION_STATUSES = ['A', 'B'];

@Component({
    selector: 'af-transaction-information',
    templateUrl: './transaction-information.component.html',
    styleUrl: './transaction-information.component.scss',
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FORMATS},
    ],
    standalone: false,
})
export class TransactionInformationComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() form: FormGroup<TransactionForm>;
    @Input() transaction: IncidentTransaction;
    @Input() formInitialized: BehaviorSubject<boolean>;
    @Input() maxFilesCount: number;
    @ViewChild('typeSelector') typeSelector!: DictionarySelectorComponent;
    @ViewChildren(DictionarySelectorComponent) dictionarySelectors!: QueryList<DictionarySelectorComponent>;
    @ViewChildren(TransactionParticipantComponent) participantComponents!: QueryList<TransactionParticipantComponent>;
    DATE_TIME_FORMATS = DATE_TIME_FORMATS;
    MIN_DATE = MIN_DATE;
    TransactionCategory = TransactionCategory;
    TransactionType = TransactionType;
    MIN_RRN_BRN_LENGTH = MIN_RRN_BRN_LENGTH;
    MAX_RRN_BRN_LENGTH = MAX_RRN_BRN_LENGTH;
    Validators = Validators;
    Spinner = Spinner;
    transactionTypes: ReferenceBookResponse[];
    filteredTypesList: ReferenceBookResponse[] = [];
    senderExcludedRequisiteTypes: string[] = [];
    receiverExcludedRequisiteTypes: string[] = [];
    BucketName = BucketName;
    isCompromisedPaymentInstrumentType: boolean = false;
    excludedCategories: string[] = ['CR'];
    excludedTransactionStatuses: string[] = [];
    loanInfoExpanded = true;

    private clientConfirmedSubscription: Subscription;

    constructor(
        private cdr: ChangeDetectorRef,
        private formBuilder: FormBuilder,
        private dictionaryHolderService: DictionaryHolderService,
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
    ) {
    }

    get typeTooltipKey(): string {
        return this.typeConfig?.tooltipKey ?? '';
    }

    get paymentTypeSelected(): boolean {
        return this.typeConfig?.kind === 'payment';
    }

    get transferTypeSelected(): boolean {
        return this.typeConfig?.kind === 'transfer';
    }

    get cashTypeSelected(): boolean {
        return this.typeConfig?.kind === 'cash';
    }

    get transferSenderSubjectType(): SubjectType | null {
        return this.transferTypeConfig?.senderSubjectType ?? null;
    }

    get receiverPrefillRequisiteType(): RequisiteType | null {
        return this.prefillRequisiteTypeOf('receiver');
    }

    get senderPrefillRequisiteType(): RequisiteType | null {
        return this.prefillRequisiteTypeOf('sender');
    }

    get cashSenderRole(): 'withdrawal-sender' | null {
        return this.typeConfig?.kind === 'cash' && this.typeConfig.senderRequisiteType ? 'withdrawal-sender' : null;
    }

    get cashReceiverRole(): 'top-up-receiver' | null {
        return this.typeConfig?.kind === 'cash' && this.typeConfig.receiverRequisiteType ? 'top-up-receiver' : null;
    }

    get senderExtraRequisiteBlocks(): RequisiteType[] {
        return this.typeConfig?.senderExtraRequisiteBlocks ?? NO_EXTRA_REQUISITE_BLOCKS;
    }

    get receiverExtraRequisiteBlocks(): RequisiteType[] {
        return this.typeConfig?.receiverExtraRequisiteBlocks ?? NO_EXTRA_REQUISITE_BLOCKS;
    }

    get transferPhoneRequired(): boolean {
        return !!this.transferTypeConfig?.phoneRequired;
    }

    get attachmentsRequired(): boolean {
        return this.authHolderService.hasAccess(['government-user']) && !!this.typeConfig?.kind;
    }

    get paymentOrganizationInvolved(): boolean {
        if (!this.paymentTypeSelected) {
            return false;
        }
        const organizationBin = this.authHolderService.organizationBin;
        return !!organizationBin && [this.form.controls.sender, this.form.controls.receiver]
            .some(participant => Object.values(REQUISITE_ISSUER_BIN_CONTROL)
                .some(controlName => participant?.get(controlName)?.value === organizationBin));
    }

    get isMiaStatusLocked(): boolean {
        return this.authHolderService.hasAccess(['government-user']) && this.isFraudIncidentType();
    }

    get isExistingTransaction(): boolean {
        return !!this.transaction;
    }

    get showRrnField(): boolean {
        const type = this.form.controls.type?.value;
        if (this.transferTypeSelected) {
            return type === TransactionType.C2C;
        }
        return type !== TransactionType.AFL;
    }

    get showPaymentIdField(): boolean {
        return this.form.controls.type?.value !== TransactionType.C2C;
    }

    private get typeConfig(): TransactionTypeConfig | undefined {
        if (!this.isFraudIncidentType()) {
            return undefined;
        }
        const type = this.form.controls.type?.value;
        return type ? TRANSACTION_TYPE_CONFIG[type] : undefined;
    }

    private get transferTypeConfig(): TransactionTypeConfig | undefined {
        const config = this.typeConfig;
        return config?.kind === 'transfer' ? config : undefined;
    }

    ngOnInit(): void {
        let date: Date;
        let time: string;
        this.isCompromisedPaymentInstrumentType = this.isCompromisedPaymentInstrumentIncidentType();
        this.excludedTransactionStatuses = this.isCompromisedPaymentInstrumentType || this.authHolderService.hasAccess(['government-user'])
            ? INCOMPLETE_TRANSACTION_STATUSES
            : [];
        if (this.transaction) {
            this.excludedCategories = [];
        } else {
            this.dictionaryHolderService.getReference('bin').subscribe(bins => {
                const code1 = bins.find(item => item.code === this.authHolderService.organizationBin)?.code1;
                const canCreateCredit = CREDIT_ORG_TYPES.includes(code1) && this.isFraudIncidentType();
                this.excludedCategories = canCreateCredit ? [] : ['CR'];
            });
        }
        if (this.transaction) {
            date = dayjs(this.transaction.date).toDate();
            time = dayjs(this.transaction.date).format('HH:mm');
        }
        if (!this.form.get('category')) {
            this.form.addControl('category', new FormControl(null, [Validators.required]));
        }
        if (!this.form.get('type')) {
            this.form.addControl('type', new FormControl(null, [Validators.required]));
        }
        if (this.isFraudIncidentType()) {
            if (!this.form.get('fraudScheme')) {
                this.form.addControl('fraudScheme', new FormControl(null, [Validators.required]));
            }
            if (!this.form.get('channel')) {
                this.form.addControl('channel', new FormControl(null, [Validators.required]));
            }
        } else {
            this.form.removeControl('fraudScheme');
            this.form.removeControl('channel');
        }
        if (!this.form.get('transactionDate')) {
            this.form.addControl('transactionDate', this.formBuilder.group<DateForm>({
                date: new FormControl(date, [Validators.required]),
                time: new FormControl(time, [Validators.required]),
            }));
            this.form.controls.transactionDate.setValidators(DateTimeValidators.dateTimeValid<DateForm>('date', 'time'));
        }
        if (!this.form.get('amount')) {
            this.form.addControl('amount', new FormControl(this.transaction?.amount, [Validators.required]));
        }
        if (!this.form.get('currency')) {
            this.form.addControl('currency', new FormControl(null, [Validators.required]));
        }
        if (!this.form.get('rrnBrn')) {
            this.form.addControl('rrnBrn', new FormControl(this.transaction?.transactionRrn, [
                Validators.minLength(MIN_RRN_BRN_LENGTH),
                Validators.maxLength(MAX_RRN_BRN_LENGTH)
            ]));
        }
        if (!this.form.get('id')) {
            this.form.addControl('id', new FormControl(this.transaction?.id));
        }
        if (!this.form.get('paymentId')) {
            this.form.addControl('paymentId', new FormControl(this.transaction?.payment_id));
        }
        if (!this.form.get('clientConfirmed')) {
            this.form.addControl('clientConfirmed', new FormControl(null, [Validators.required]));
        }
        if (!this.form.get('status')) {
            this.form.addControl('status', new FormControl(null, [Validators.required]));
        }
        if (this.transaction && this.form.get('status')?.hasValidator(Validators.required)) {
            this.form.get('status')?.markAsTouched();
        }
        if (this.isMiaStatusLocked) {
            this.form.controls.status.setValue(COMPLETED_TRANSACTION_STATUS, {emitEvent: false});
            this.form.controls.status.disable({emitEvent: false});
        }
        if (!this.form.get('isDeleted')) {
            this.form.addControl('isDeleted', new FormControl(null));
        }
        if (!this.form.get('attachments')) {
            this.form.addControl('attachments', this.formBuilder.group({}));
        }
        if (!this.form.get('sender')  && !this.isCompromisedPaymentInstrumentType) {
            this.form.addControl('sender', this.formBuilder.group({}, {validators: requiredFormGroupValidator()}));
        }
        if (!this.form.get('receiver')) {
            this.form.addControl('receiver', this.formBuilder.group({}, {validators: requiredFormGroupValidator()}));
        }
        this.dictionaryHolderService.getReference('transaction_type').subscribe(items => {
            this.transactionTypes = items;
            this.updateFilteredTypes(this.form.controls.category.value);
        });
        this.form.controls.category.valueChanges.subscribe(category => {
            this.updateFilteredTypes(category);
            this.toggleCategoryForms(category);
            this.applyCashChannel();
        });
        this.form.controls.type.valueChanges.subscribe(type => {
            this.updateExcludedRequisiteTypes(type);
            this.applyPaymentPrefill(type);
            this.applyTransferPrefill(type);
            this.applyCashPrefill(type);
            this.applyRrnFieldState();
        });
        this.applyRrnFieldState();
        this.toggleCategoryForms(this.form.controls.category.value);

        this.subscribeToClientConfirmed();

        this.cdr.detectChanges();

        if (this.transaction) {
            this.amountCurrencyCrossValidation();
            this.formatDisplayedDate();
            this.form.controls.category.disable({emitEvent: false});
            this.form.controls.type.disable({emitEvent: false});
            this.applyCashChannel();
            this.updateExcludedRequisiteTypes(this.form.controls.type.value);
            this.updateIdentifierValidators(this.form.controls.type.value);
            this.updateAttachmentsValidator(this.form.controls.type.value);
        }
    }

    ngAfterViewInit(): void {
        this.syncTypeSelector();
        this.dictionarySelectors.changes.subscribe(() => {
            this.syncTypeSelector();
        });
    }

    ngOnDestroy(): void {
        this.clientConfirmedSubscription?.unsubscribe();
    }

    private applyRrnFieldState(): void {
        const rrnControl = this.form.controls.rrnBrn;
        if (!rrnControl) {
            return;
        }
        if (this.showRrnField) {
            rrnControl.enable({emitEvent: false});
        } else {
            rrnControl.disable({emitEvent: false});
        }
    }

    private updateFilteredTypes(category: string): void {
        if (!this.transactionTypes || this.transactionTypes.length === 0) {
            this.filteredTypesList = [];
            return;
        }
        const newList = category
            ? this.transactionTypes.filter(t => t.code1 === category)
            : [];
        const currentType = this.form.controls.type.value;
        if (currentType && !newList.some(item => item.code === currentType)) {
            this.form.controls.type.setValue(null);
        }
        this.filteredTypesList = [...newList];
        if (this.typeSelector) {
            this.typeSelector.filteredDictionary = this.filteredTypesList;
            this.typeSelector.selectedItems = [];
            this.typeSelector.updateDisplayedValue();
        }
        this.syncTypeSelector();
    }

    private syncTypeSelector(): void {
        if (!this.dictionarySelectors || this.dictionarySelectors.length === 0) {
            return;
        }
        const typeSelector = this.dictionarySelectors.find(s => s.control === this.form.controls.type);
        if (typeSelector) {
            typeSelector.filteredDictionary = [...this.filteredTypesList];
            const currentValue = this.form.controls.type.value ?? this.transaction?.type;
            const match = this.filteredTypesList.find(item => item.code === currentValue || item.name_ru === currentValue);
            if (match) {
                typeSelector.selectedItems = [match];
            } else {
                typeSelector.selectedItems = [];
            }
            typeSelector.updateDisplayedValue();
        }
    }

    private updateExcludedRequisiteTypes(type: string): void {
        const config = type ? this.typeConfig : undefined;
        this.senderExcludedRequisiteTypes =
            this.excludedRequisiteTypesFrom(config?.senderAllowedRequisiteTypes, this.transaction?.sender?.requisiteType);
        this.receiverExcludedRequisiteTypes =
            this.excludedRequisiteTypesFrom(config?.receiverAllowedRequisiteTypes, this.transaction?.receiver?.requisiteType);
    }

    private excludedRequisiteTypesFrom(allowed?: RequisiteType[], savedRequisiteType?: RequisiteType): string[] {
        if (!allowed) {
            return NO_EXCLUDED_REQUISITE_TYPES;
        }
        return ALL_REQUISITE_TYPES.filter(requisiteType => !allowed.includes(requisiteType) && requisiteType !== savedRequisiteType);
    }

    private prefillRequisiteTypeOf(side: 'sender' | 'receiver'): RequisiteType | null {
        const config = this.typeConfig;
        if (config?.kind !== 'transfer' && config?.kind !== 'cash') {
            return null;
        }
        if (config.miaRequisitePrefillSkipped && this.authHolderService.hasAccess(['government-user'])) {
            return null;
        }
        return (side === 'sender' ? config.senderRequisiteType : config.receiverRequisiteType) ?? null;
    }

    private applyPaymentPrefill(type: string): void {
        const config = type ? this.typeConfig : undefined;
        if (config?.kind !== 'payment' || this.transaction) {
            return;
        }
        this.prefillParticipant(this.form.controls.sender, SubjectType.PERSON, config.senderRequisiteType);
        this.prefillParticipant(this.form.controls.receiver, SubjectType.ORGANIZATION, config.receiverRequisiteType);
        this.cdr.detectChanges();
    }

    private applyTransferPrefill(type: string): void {
        this.updateIdentifierValidators(type);
        this.updateAttachmentsValidator(type);
        if (this.transaction) {
            return;
        }
        const config = type ? this.typeConfig : undefined;
        if (config?.kind !== 'transfer') {
            return;
        }
        const senderRequisiteType = this.senderPrefillRequisiteType;
        if (config.senderSubjectType && senderRequisiteType) {
            this.prefillParticipant(this.form.controls.sender, config.senderSubjectType, senderRequisiteType);
        } else {
            this.form.controls.sender?.controls.requisiteType?.setValue(senderRequisiteType);
        }
        this.clearRequisites(this.form.controls.sender);

        this.clearRequisites(this.form.controls.receiver);
        this.form.controls.receiver?.controls.requisiteType?.setValue(this.receiverPrefillRequisiteType);
        this.cdr.detectChanges();
    }

    private applyCashPrefill(type: string): void {
        const config = type ? this.typeConfig : undefined;
        if (config?.kind !== 'cash' || this.transaction) {
            return;
        }
        if (config.senderRequisiteType) {
            this.form.controls.sender?.controls.requisiteType?.setValue(config.senderRequisiteType);
        }
        if (config.receiverRequisiteType) {
            this.form.controls.receiver?.controls.requisiteType?.setValue(config.receiverRequisiteType);
        }
        this.cdr.detectChanges();
    }

    private clearRequisites(form?: FormGroup<ParticipantForm>): void {
        if (form) {
            form.get('iban')?.setValue(null);
            form.get('cardMaskNumber')?.setValue(null);
            form.get('cardIssuerBin')?.setValue(null);
        }
    }

    private applyCashChannel(): void {
        const channelControl = this.form.controls.channel;
        if (!channelControl) {
            return;
        }
        if (this.isFraudIncidentType() && this.form.controls.category.value === CASH_TRANSACTION_CATEGORY) {
            channelControl.setValue('OFFLINE', {emitEvent: false});
            channelControl.disable({emitEvent: false});
        } else if (channelControl.disabled) {
            channelControl.enable({emitEvent: false});
        }
    }

    private updateIdentifierValidators(type: string): void {
        const rrnControl = this.form.controls.rrnBrn;
        const paymentIdControl = this.form.controls.paymentId;
        if (!rrnControl || !paymentIdControl) {
            return;
        }
        rrnControl.removeValidators(Validators.required);
        paymentIdControl.removeValidators(Validators.required);
        if (type && this.isFraudIncidentType()) {
            if (this.typeConfig?.kind === 'transfer') {
                if (type === TransactionType.C2C) {
                    rrnControl.addValidators(Validators.required);
                    paymentIdControl.setValue(null, {emitEvent: false});
                } else {
                    paymentIdControl.addValidators(Validators.required);
                    rrnControl.setValue(null, {emitEvent: false});
                }
            } else if (this.typeConfig?.kind === 'payment') {
                paymentIdControl.addValidators(Validators.required);
            }
        }
        rrnControl.updateValueAndValidity({emitEvent: false});
        paymentIdControl.updateValueAndValidity({emitEvent: false});
    }

    private updateAttachmentsValidator(type: string): void {
        const attachmentsForm = this.form.controls.attachments;
        if (!attachmentsForm || !this.authHolderService.hasAccess(['government-user'])) {
            return;
        }
        const kind = type ? this.typeConfig?.kind : undefined;
        if (kind) {
            attachmentsForm.setValidators(attachmentsRequiredValidator());
        } else {
            attachmentsForm.clearValidators();
        }
        attachmentsForm.updateValueAndValidity({emitEvent: false});
    }

    private prefillParticipant(
        form: FormGroup<ParticipantForm> | undefined,
        subjectType: SubjectType,
        requisiteType: RequisiteType | undefined,
    ): void {
        if (!form) {
            return;
        }
        if (!form.controls.subjectType) {
            this.participantComponents?.find(component => component.form === form)?.buildForm();
        }
        if (!form.controls.subjectType) {
            return;
        }
        form.controls.subjectType.setValue(subjectType);
        if (requisiteType) {
            form.controls.requisiteType?.setValue(requisiteType);
        }
    }

    private formatDisplayedDate(): void {
        const startDate = dayjs(this.form.controls.transactionDate.controls.date.value);
        let dateWithTime: Date;
        if (startDate && this.form.controls.transactionDate.controls.time.value) {
            const [hours, minutes] = this.form.controls.transactionDate.controls.time.value.split(':').map(Number);
            dateWithTime = startDate.hour(hours).minute(minutes).toDate();
        } else {
            dateWithTime = startDate.hour(0).minute(0).toDate();
        }
        this.form.controls.transactionDate.controls.date.setValue(dateWithTime);
    }

    private amountCurrencyCrossValidation(): void {
        const amountControl = this.form.get('amount');
        const currencyControl = this.form.get('currency');
        if (!amountControl || !currencyControl) {
            return;
        }
        amountControl.valueChanges.subscribe((amountValue) => {
            const shouldRequireCurrency = !!amountValue;

            if (shouldRequireCurrency) {
                currencyControl.setValidators([Validators.required]);
                currencyControl.markAsTouched();
            } else {
                currencyControl.clearValidators();
            }
            currencyControl.updateValueAndValidity();
        });
    }

    private isCompromisedPaymentInstrumentIncidentType(): boolean {
        return this.incidentTypeCode() === IncidentType.COMPROMISED_PAYMENT_INSTRUMENT;
    }

    private isFraudIncidentType(): boolean {
        return this.incidentTypeCode() === IncidentType.FRAUD;
    }

    private incidentTypeCode(): string {
        return this.form.root?.get('commonInformationForm.type')?.value ??
            this.activatedRoute.snapshot.queryParamMap.get(INCIDENT_TYPE_CODE);
    }

    private subscribeToClientConfirmed(): void {
        this.clientConfirmedSubscription?.unsubscribe();
        this.clientConfirmedSubscription = this.form.controls.clientConfirmed.valueChanges.subscribe(value => {
            this.handleClientConfirmedChanges(value);
            this.form.updateValueAndValidity();
            this.cdr.detectChanges();
        });
    }

    private handleClientConfirmedChanges(value: string): void {
        if (value === 'true') {
            if (!this.form.get('confirmationMethod')) {
                const confirmationMethodControl = new FormControl(this.transaction?.confirmationMethod, [Validators.required]);
                this.form.addControl('confirmationMethod', confirmationMethodControl);
                confirmationMethodControl.markAsTouched();
            }
            if (!this.form.get('clientLegitimacyConfirmation')) {
                const clientLegitimacyConfirmationControl = new FormControl(
                    this.transaction?.clientLegitimacyConfirmation,
                    [Validators.required]
                );
                this.form.addControl('clientLegitimacyConfirmation', clientLegitimacyConfirmationControl);
                clientLegitimacyConfirmationControl.markAsTouched();
            }
        } else {
            if (this.form.get('confirmationMethod')) {
                this.form.removeControl('confirmationMethod');
            }
            if (this.form.get('clientLegitimacyConfirmation')) {
                this.form.removeControl('clientLegitimacyConfirmation');
            }
        }
    }

    private toggleCategoryForms(category: string): void {
        const merchantInfoAllowed = this.authHolderService.hasAccess(['npck-user', 'finance-user']);
        const merchantInfoRequired = this.authHolderService.hasAccess(['finance-user']);
        this.toggleCategoryControl(category === TransactionCategory.CR, 'loanInfo', () => buildLoanInfoForm(this.formBuilder));
        this.toggleCategoryControl(
            category === TransactionCategory.A && merchantInfoAllowed,
            'merchantInfo',
            () => buildMerchantInfoForm(this.formBuilder, merchantInfoRequired, this.transaction?.merchantInfo),
        );
        this.cdr.detectChanges();
    }

    private toggleCategoryControl<K extends 'loanInfo' | 'merchantInfo'>(
        active: boolean,
        controlName: K,
        build: () => Required<TransactionForm>[K],
    ): void {
        if (active) {
            if (!this.form.get(controlName)) {
                this.form.addControl(controlName, build());
            }
        } else if (this.form.get(controlName)) {
            this.form.removeControl(controlName);
        }
    }

}

export interface TransactionForm {
    category?: FormControl<string>;
    type?: FormControl<string>;
    fraudScheme?: FormControl<string>;
    channel?: FormControl<string>;
    transactionDate?: FormGroup<DateForm>;
    amount?: FormControl<number>;
    currency?: FormControl<string>;
    rrnBrn?: FormControl<string>;
    id?: FormControl<string>;
    paymentId?: FormControl<string>;
    clientConfirmed?: FormControl<string>;
    confirmationMethod?: FormControl<ConfirmationMethod>;
    clientLegitimacyConfirmation?: FormControl<ClientLegitimacyConfirmation>;
    status?: FormControl<string>;
    isDeleted?: FormControl<boolean>;
    attachments?: FormGroup<AttachmentsForm>;
    sender?: FormGroup<ParticipantForm>;
    receiver?: FormGroup<ParticipantForm>;
    loanInfo?: FormGroup<LoanInfoForm>;
    merchantInfo?: FormGroup<MerchantInfoForm>;
}

export interface LoanInfoForm {
    type?: FormControl<string>;
    status?: FormControl<string>;
    documentNumber?: FormControl<string>;
    startDate?: FormGroup<DateForm>;
    endDate?: FormGroup<DateForm>;
    actualRepaymentDate?: FormGroup<DateForm>;
    applicationDate?: FormGroup<DateForm>;
    amount?: FormControl<number>;
    amountCurrency?: FormControl<string>;
    balance?: FormControl<number>;
    balanceCurrency?: FormControl<string>;
    suspensionReason?: FormControl<string>;
    suspensionDate?: FormGroup<DateForm>;
    forgivenessAmount?: FormControl<number>;
    forgivenessCurrency?: FormControl<string>;
    forgivenessDate?: FormGroup<DateForm>;
    forgivenessReason?: FormControl<string>;
    isSuspended?: FormControl<boolean>;
    isForgiven?: FormControl<boolean>;
    suspensionDocuments?: FormGroup<AttachmentsForm>;
    forgivenessDocuments?: FormGroup<AttachmentsForm>;
    borrowerCategory?: FormControl<string>;
}

export function loanForgivenessAmountValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const amount = control.get('forgivenessAmount')?.value;
        if (amount == null) {
            return null;
        }
        const balance = control.get('balance')?.value;
        const loanAmount = control.get('amount')?.value;
        if (Number(amount) <= 0
            || (balance != null && Number(amount) > Number(balance))
            || (loanAmount != null && Number(amount) > Number(loanAmount))) {
            return {forgivenessAmountInvalid: true};
        }
        return null;
    };
}

export function requiredFormGroupValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (
            !control || !(control instanceof Object) ||
            Object.keys((control as any).controls).length === 0 ||
            (control as FormGroup<ParticipantForm>).invalid
        ) {
            return {required: true};
        }
        return null;
    };
}
export function createTransactionRequest(transactionForm: FormGroup<TransactionForm>): IncidentTransaction {
    const rawTransaction = transactionForm.getRawValue();
    return <IncidentTransaction>{
        id: rawTransaction.id,
        payment_id: rawTransaction.paymentId,
        type: rawTransaction.type,
        status: rawTransaction.status,
        currency: sanitizeDigits(rawTransaction.currency),
        transactionRrn: rawTransaction.rrnBrn,
        date: getFormattedDateOrNull(rawTransaction.transactionDate.date),
        amount: Number(rawTransaction.amount),
        clientConfirmed: rawTransaction.clientConfirmed?.toLowerCase() === 'true',
        confirmationMethod: rawTransaction.confirmationMethod,
        clientLegitimacyConfirmation: rawTransaction.clientLegitimacyConfirmation,
        fraudScheme: rawTransaction.fraudScheme,
        channel: rawTransaction.channel,
        loanInfo: transactionForm.controls.loanInfo ? createLoanInfoRequest(transactionForm.controls.loanInfo) : undefined,
        merchantInfo: transactionForm.controls.merchantInfo ? createMerchantInfoRequest(transactionForm.controls.merchantInfo) : undefined,
        is_deleted: rawTransaction.isDeleted,
        attachments: createAttachmentsRequest(transactionForm.controls.attachments?.value, rawTransaction.isDeleted),
        sender: rawTransaction.sender
            ? createParticipantRequest(rawTransaction.sender, rawTransaction.isDeleted)
            : null,
        receiver: createParticipantRequest(rawTransaction.receiver, rawTransaction.isDeleted),
    };
}

export function normalizeBlankStringsToUndefined<T>(value: T): T {
    if (typeof value === 'string') {
        return (value.trim().length === 0 ? undefined : value) as T;
    }
    if (Array.isArray(value)) {
        return value.map(item => normalizeBlankStringsToUndefined(item)) as T;
    }
    if (value !== null && typeof value === 'object' && value.constructor === Object) {
        const normalized: Record<string, unknown> = {};
        Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
            normalized[key] = normalizeBlankStringsToUndefined(val);
        });
        return normalized as T;
    }
    return value;
}

export function createTransactionRequestV2(transactionForm: FormGroup<TransactionForm>): IncidentTransactionRequestV2 {
    const rawTransaction = transactionForm.getRawValue();
    return <IncidentTransactionRequestV2>{
        id: rawTransaction.id,
        isDeleted: rawTransaction.isDeleted,
        type: rawTransaction.type,
        status: rawTransaction.status,
        amount: Number(rawTransaction.amount),
        currency: sanitizeDigits(rawTransaction.currency),
        date: getFormattedDateOrNull(rawTransaction.transactionDate.date),
        rrn: rawTransaction.rrnBrn,
        paymentId: rawTransaction.paymentId,
        clientConfirmed: String(rawTransaction.clientConfirmed).toLowerCase() === 'true',
        confirmationMethod: rawTransaction.confirmationMethod,
        clientLegitimacyConfirmation: rawTransaction.clientLegitimacyConfirmation,
        fraudScheme: rawTransaction.fraudScheme,
        channel: rawTransaction.channel,
        loanInfo: transactionForm.controls.loanInfo ? createLoanInfoRequest(transactionForm.controls.loanInfo) : undefined,
        merchantInfo: transactionForm.controls.merchantInfo ? createMerchantInfoRequest(transactionForm.controls.merchantInfo) : undefined,
        sender: rawTransaction.sender ? createParticipantRequestV2(rawTransaction.sender) : null,
        receiver: createParticipantRequestV2(rawTransaction.receiver),
        attachments: createAttachmentsRequestV2(transactionForm.controls.attachments?.value),
    };
}

export function buildLoanInfoForm(
    formBuilder: FormBuilder,
    loanInfo?: LoanInfoRequest,
    currencyDictionary?: ReferenceBookResponse[],
    attachedDocDictionary?: ReferenceBookResponse[],
): FormGroup<LoanInfoForm> {
    const currencyValue = (code?: string): string =>
        currencyDictionary && code ? findValueInDictionary(currencyDictionary, code) : null;
    const group = formBuilder.group<LoanInfoForm>({
        type: new FormControl(loanInfo?.type ?? null, [Validators.required]),
        status: new FormControl(loanInfo?.status ?? null, [Validators.required]),
        documentNumber: new FormControl(loanInfo?.documentNumber ?? null, [Validators.required, Validators.maxLength(50)]),
        startDate: buildDateGroup(formBuilder, loanInfo?.startDate),
        endDate: buildDateGroup(formBuilder, loanInfo?.endDate),
        actualRepaymentDate: buildDateGroup(formBuilder, loanInfo?.actualRepaymentDate),
        applicationDate: buildDateGroup(formBuilder, loanInfo?.applicationDate),
        amount: new FormControl(loanInfo?.amount ?? null, [Validators.required, Validators.min(0.01)]),
        amountCurrency: new FormControl(currencyValue(loanInfo?.amountCurrency), [Validators.required]),
        balance: new FormControl(loanInfo?.balance ?? null, [Validators.required, Validators.min(0)]),
        balanceCurrency: new FormControl(currencyValue(loanInfo?.balanceCurrency), [Validators.required]),
        suspensionReason: new FormControl(loanInfo?.suspensionReason ?? null),
        suspensionDate: buildDateGroup(formBuilder, loanInfo?.suspensionDate),
        forgivenessAmount: new FormControl(loanInfo?.forgivenessAmount ?? null),
        forgivenessCurrency: new FormControl(currencyValue(loanInfo?.forgivenessCurrency)),
        forgivenessDate: buildDateGroup(formBuilder, loanInfo?.forgivenessDate),
        forgivenessReason: new FormControl(loanInfo?.forgivenessReason ?? null),
        isSuspended: new FormControl(loanInfo?.isSuspended ?? false),
        isForgiven: new FormControl(loanInfo?.isForgiven ?? false),
        suspensionDocuments: buildLoanDocuments(formBuilder, loanInfo, 'LOAN_SUSPENSION', attachedDocDictionary),
        forgivenessDocuments: buildLoanDocuments(formBuilder, loanInfo, 'LOAN_FORGIVENESS', attachedDocDictionary),
        borrowerCategory: new FormControl(loanInfo?.borrowerCategory ?? null, [Validators.required]),
    }, {validators: [loanForgivenessAmountValidator()]});
    setupFlagDrivenRequired(group, 'isSuspended', ['suspensionReason', 'suspensionDate'], 'suspensionDocuments');
    setupFlagDrivenRequired(group, 'isForgiven',
        ['forgivenessReason', 'forgivenessAmount', 'forgivenessCurrency', 'forgivenessDate'], 'forgivenessDocuments');
    return group;
}

function buildLoanDocuments(
    formBuilder: FormBuilder,
    loanInfo: LoanInfoRequest | undefined,
    docType: string,
    attachedDocDictionary: ReferenceBookResponse[] | undefined,
): FormGroup<AttachmentsForm> {
    if (!loanInfo || !attachedDocDictionary) {
        return formBuilder.group({});
    }
    const documents = (loanInfo.attachments ?? [])
        .filter(attachment => attachment.docType === docType)
        .map(attachment => <DocumentAttachment>{docType: attachment.docType, files: attachment.files});
    return buildAttachmentsForm(formBuilder, documents, attachedDocDictionary, true);
}

function setupFlagDrivenRequired(group: FormGroup<LoanInfoForm>, flagKey: string, keys: string[], documentsKey: string): void {
    const flagControl = group.get(flagKey);
    if (!flagControl) {
        return;
    }
    const apply = (on: boolean): void => {
        keys.forEach(key => {
            const nested = group.get(key);
            const control = nested instanceof FormGroup ? nested.get('date') : nested;
            if (!control) {
                return;
            }
            if (on && !control.hasValidator(Validators.required)) {
                control.addValidators(Validators.required);
            } else if (!on) {
                control.removeValidators(Validators.required);
                control.reset(null, {emitEvent: false});
            }
            control.updateValueAndValidity({emitEvent: false});
        });
        if (!on) {
            const documents = group.get(documentsKey);
            documents?.clearValidators();
            (documents?.get('attachments') as FormArray)?.clear();
            documents?.updateValueAndValidity({emitEvent: false});
        }
    };
    apply(!!flagControl.value);
    flagControl.valueChanges.subscribe(apply);
}

function buildDateGroup(formBuilder: FormBuilder, date?: string): FormGroup<DateForm> {
    return formBuilder.group<DateForm>({
        date: new FormControl(date ? dayjs(date).toDate() : null),
    });
}

export function createLoanInfoRequest(loanInfoForm: FormGroup<LoanInfoForm>): LoanInfoRequest {
    const raw = loanInfoForm.getRawValue();
    return <LoanInfoRequest>{
        type: raw.type,
        status: raw.status,
        documentNumber: raw.documentNumber,
        startDate: getFormattedDateOrNull(raw.startDate?.date, DATE_SERVER_FORMAT),
        endDate: getFormattedDateOrNull(raw.endDate?.date, DATE_SERVER_FORMAT),
        actualRepaymentDate: getFormattedDateOrNull(raw.actualRepaymentDate?.date, DATE_SERVER_FORMAT),
        applicationDate: getFormattedDateOrNull(raw.applicationDate?.date, DATE_SERVER_FORMAT),
        amount: Number(raw.amount),
        amountCurrency: sanitizeDigits(raw.amountCurrency),
        balance: Number(raw.balance),
        balanceCurrency: sanitizeDigits(raw.balanceCurrency),
        isSuspended: !!raw.isSuspended,
        suspensionReason: raw.suspensionReason,
        suspensionDate: getFormattedDateOrNull(raw.suspensionDate?.date, DATE_SERVER_FORMAT),
        isForgiven: !!raw.isForgiven,
        forgivenessAmount: raw.forgivenessAmount != null ? Number(raw.forgivenessAmount) : undefined,
        forgivenessCurrency: sanitizeDigits(raw.forgivenessCurrency),
        forgivenessDate: getFormattedDateOrNull(raw.forgivenessDate?.date, DATE_SERVER_FORMAT),
        forgivenessReason: raw.forgivenessReason,
        borrowerCategory: raw.borrowerCategory,
        attachments: [
            ...(raw.isSuspended ? createAttachmentsRequestV2(loanInfoForm.controls.suspensionDocuments?.value) ?? [] : []),
            ...(raw.isForgiven ? createAttachmentsRequestV2(loanInfoForm.controls.forgivenessDocuments?.value) ?? [] : []),
        ],
    };
}
